import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { getAgentUrl } from '../../services/agent-url'

interface BugReport {
  id: string
  user_id: string
  user_email: string | null
  title: string
  description: string
  severity: string
  page_url: string | null
  status: string
  admin_notes: string | null
  created_at: string
  updated_at: string
}

export default function BugReportsPanel() {
  const { session } = useAuth()
  const [reports, setReports] = useState<BugReport[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    loadReports()
  }, [])

  async function loadReports() {
    setLoading(true)
    try {
      const baseUrl = await getAgentUrl()
      const res = await fetch(`${baseUrl}/api/admin/bug-reports`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setReports(data.reports)
      }
    } catch { /* ignore */ }
    setLoading(false)
  }

  async function updateStatus(id: string, status: string) {
    try {
      const baseUrl = await getAgentUrl()
      await fetch(`${baseUrl}/api/admin/bug-reports/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ status }),
      })
      setReports(prev => prev.map(r => r.id === id ? { ...r, status } : r))
    } catch { /* ignore */ }
  }

  const severityColor = (s: string) => {
    switch (s) {
      case 'high': return 'text-red-400 bg-red-500/10 border-red-500/30'
      case 'medium': return 'text-amber-400 bg-amber-500/10 border-amber-500/30'
      default: return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30'
    }
  }

  const statusColor = (s: string) => {
    switch (s) {
      case 'closed': return 'text-slate-500'
      case 'in_progress': return 'text-amber-400'
      default: return 'text-emerald-400'
    }
  }

  if (loading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading bug reports...</p>
  }

  return (
    <div className="space-y-3">
      <p className="font-mono text-xs text-slate-500">{reports.length} report{reports.length !== 1 ? 's' : ''}</p>

      {reports.length === 0 ? (
        <p className="font-mono text-xs text-slate-600">No bug reports yet</p>
      ) : (
        <div className="space-y-2">
          {reports.map(report => (
            <div
              key={report.id}
              className="glass rounded-xl border border-cyan-500/10 overflow-hidden"
            >
              {/* Header row */}
              <button
                onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] border ${severityColor(report.severity)}`}>
                  {report.severity}
                </span>
                <span className="font-mono text-sm text-slate-200 flex-1 truncate">{report.title}</span>
                <span className={`font-mono text-[10px] ${statusColor(report.status)}`}>{report.status}</span>
                <span className="font-mono text-[10px] text-slate-600">
                  {new Date(report.created_at).toLocaleDateString()}
                </span>
              </button>

              {/* Expanded detail */}
              {expandedId === report.id && (
                <div className="px-4 pb-4 border-t border-cyan-500/10 pt-3 space-y-3">
                  <div className="font-mono text-xs text-slate-400 whitespace-pre-wrap">{report.description}</div>
                  <div className="flex items-center gap-4 font-mono text-[10px] text-slate-600">
                    <span>User: {report.user_email || report.user_id.slice(0, 8)}</span>
                    {report.page_url && <span>Page: {report.page_url}</span>}
                  </div>
                  <div className="flex gap-2">
                    {['open', 'in_progress', 'closed'].map(s => (
                      <button
                        key={s}
                        onClick={() => updateStatus(report.id, s)}
                        className={`px-2 py-1 rounded font-mono text-[10px] border transition-all ${
                          report.status === s
                            ? 'border-cyan-500/40 bg-cyan-500/15 text-cyan-400'
                            : 'border-slate-700 text-slate-500 hover:border-slate-600'
                        }`}
                      >
                        {s.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
